import { Typography } from '@recruit-robin/robin-theme/web-components';
import { AlertCircle, InfoCircle } from '@recruit-robin/robin-theme/web-icons';

import { useStyles } from './BulkProgressBar.styles';

export type BulkProgressBarLabels = {
  inProgress: string;
  allDone: string;
  nFailed: (n: number) => string;
  doneWithFailures: (n: number) => string;
};

export type BulkProgressBarProps = {
  completed: number;
  failed: number;
  total: number;
  isComplete: boolean;
  labels: BulkProgressBarLabels;
};

export function BulkProgressBar({ completed, failed, total, isComplete, labels }: BulkProgressBarProps) {
  const classes = useStyles();

  const processed = completed + failed;
  const fillPercent = total > 0 ? (processed / total) * 100 : 0;
  const isAllSuccess = isComplete && failed === 0;
  const hasFailures = failed > 0;

  const statusLabel = !isComplete ? labels.inProgress : isAllSuccess ? labels.allDone : labels.doneWithFailures(failed);

  const countLabel = isComplete ? `${completed}/${total}` : `${processed}/${total}`;

  return (
    <div css={classes.container}>
      <div css={classes.track}>
        <div
          css={[classes.fill, isAllSuccess ? classes.fillEmerald : classes.fillFuchsia]}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
      <div css={classes.labelRow}>
        <div css={classes.statusGroup}>
          <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
            {statusLabel}
          </Typography>
          {isComplete && hasFailures && <InfoCircle size={14} color={(c) => c.secondary.sky[60]} />}
        </div>
        <div css={classes.rightGroup}>
          {!isComplete && hasFailures && (
            <div css={classes.failedBadge}>
              <AlertCircle size={14} color={(c) => c.secondary.sky[60]} />
              <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
                {labels.nFailed(failed)}
              </Typography>
            </div>
          )}
          <Typography variant={(v) => v.supporting.helper} color={(c) => c.neutral.variant[70]}>
            {countLabel}
          </Typography>
        </div>
      </div>
    </div>
  );
}
